import React, { useState, useEffect } from 'react';
import { Editor, EditorState, convertToRaw, convertFromRaw, RichUtils, Modifier, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'draft-js/dist/Draft.css';

const AmplifierForm = ({
  amplifier,
  manufacturers,
  templates,
  onSave,
  onCancel,
  parentAmplifiers = [] // Add support for parent selection
}) => {
  const [manufacturerId, setManufacturerId] = useState(amplifier?.manufacturer_id || '');
  const [templateId, setTemplateId] = useState(amplifier?.template_id || '');
  const [name, setName] = useState(amplifier?.name || '');
  const [model, setModel] = useState(amplifier?.model || '');
  const [startYear, setStartYear] = useState(amplifier?.start_year || '');
  const [endYear, setEndYear] = useState(amplifier?.end_year || '');
  const [serialNumber, setSerialNumber] = useState(amplifier?.serial_number || '');
  const [isParent, setIsParent] = useState(amplifier?.is_parent !== undefined ? amplifier.is_parent : true);
  const [parentId, setParentId] = useState(amplifier?.parent_id || '');
  // Description as Draft.js EditorState
  const [editorState, setEditorState] = useState(() => {
    if (amplifier?.description) {
      try {
        return EditorState.createWithContent(convertFromRaw(JSON.parse(amplifier.description)));
      } catch {
        return EditorState.createEmpty();
      }
    }
    return EditorState.createEmpty();
  });
  
  // Font size state
  const [fontSize, setFontSize] = useState(14);
  
  // Tab state for Visual/HTML toggle
  const [activeTab, setActiveTab] = useState('visual'); // 'visual' or 'html'
  const [htmlContent, setHtmlContent] = useState('');
  const [useRawHtmlMode, setUseRawHtmlMode] = useState(false); // When true, stores raw HTML instead of Draft.js
  const [htmlDirty, setHtmlDirty] = useState(false); // Track if HTML was modified
  
  // Convert Draft.js to HTML for the HTML tab
  const getHtmlFromEditorState = (state) => {
    const rawContentState = convertToRaw(state.getCurrentContent());
    return draftToHtml(rawContentState);
  };
  
  // Convert HTML to Draft.js EditorState
  const setEditorStateFromHtml = (html) => {
    const blocksFromHtml = htmlToDraft(html);
    if (blocksFromHtml) {
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  };
  
  // Handle tab switching
  const handleTabSwitch = (tab) => {
    if (tab === 'html' && activeTab === 'visual') {
      // Switching from visual to HTML - update HTML content from editor
      setHtmlContent(getHtmlFromEditorState(editorState));
      setHtmlDirty(false);
    } else if (tab === 'visual' && activeTab === 'html') {
      // Switching from HTML to visual - warn if HTML was modified
      if (htmlDirty && !useRawHtmlMode) {
        const confirmed = window.confirm(
          'Warning: Switching to Visual mode will convert your HTML to a simplified format.\n\n' +
          'Complex formatting (tables, custom styles, divs, etc.) may be lost or altered.\n\n' +
          'To preserve your exact HTML, check "Use Raw HTML Mode" checkbox instead.\n\n' +
          'Continue switching to Visual mode?'
        );
        if (!confirmed) {
          return; // Don't switch tabs
        }
      }
      setEditorState(setEditorStateFromHtml(htmlContent));
    }
    setActiveTab(tab);
  };
  
  // Handle HTML content change
  const handleHtmlChange = (e) => {
    setHtmlContent(e.target.value);
    setHtmlDirty(true);
  };

  useEffect(() => {
    if (amplifier?.description) {
      // Check if description is raw HTML (has our marker prefix)
      if (amplifier.description.startsWith('__RAW_HTML__')) {
        const rawHtml = amplifier.description.replace('__RAW_HTML__', '');
        setHtmlContent(rawHtml);
        setUseRawHtmlMode(true);
        setActiveTab('html');
        // Also set the editor state from the HTML for visual mode
        setEditorState(setEditorStateFromHtml(rawHtml));
      } else {
        try {
          setEditorState(EditorState.createWithContent(convertFromRaw(JSON.parse(amplifier.description))));
          setUseRawHtmlMode(false);
        } catch {
          setEditorState(EditorState.createEmpty());
        }
      }
    }
    
    // Update all fields when amplifier changes
    if (amplifier) {
      setStartYear(amplifier.start_year || '');
      setEndYear(amplifier.end_year || '');
      setIsParent(amplifier.is_parent !== undefined ? amplifier.is_parent : true);
      setParentId(amplifier.parent_id || '');
    }
  }, [amplifier]);

  // Bold toggle function
  const toggleBold = () => {
    const selection = editorState.getSelection();
    const newEditorState = RichUtils.toggleInlineStyle(editorState, 'BOLD');
    const finalEditorState = EditorState.forceSelection(newEditorState, selection);
    setEditorState(finalEditorState);
  };

  // Italic toggle function
  const toggleItalic = () => {
    const selection = editorState.getSelection();
    const newEditorState = RichUtils.toggleInlineStyle(editorState, 'ITALIC');
    const finalEditorState = EditorState.forceSelection(newEditorState, selection);
    setEditorState(finalEditorState);
  };

  // Underline toggle function
  const toggleUnderline = () => {
    const selection = editorState.getSelection();
    const newEditorState = RichUtils.toggleInlineStyle(editorState, 'UNDERLINE');
    const finalEditorState = EditorState.forceSelection(newEditorState, selection);
    setEditorState(finalEditorState);
  };

  // Apply font size to selected text
  const applyFontSize = (size) => {
    const selection = editorState.getSelection();
    
    if (selection.isCollapsed()) {
      // If no text is selected, just update the current style for future typing
      return;
    }
    
    const contentState = editorState.getCurrentContent();
    
    // Remove all existing font size styles first
    const fontSizeStyles = Object.keys(styleMap).filter(style => style.startsWith('FONTSIZE_'));
    let newContentState = contentState;
    
    fontSizeStyles.forEach(style => {
      newContentState = Modifier.removeInlineStyle(newContentState, selection, style);
    });
    
    // Apply the new font size styling
    newContentState = Modifier.applyInlineStyle(
      newContentState,
      selection,
      `FONTSIZE_${size}`
    );
    
    // Create new editor state and preserve selection
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'change-inline-style'
    );
    
    // Force the selection to be maintained
    const finalEditorState = EditorState.forceSelection(newEditorState, selection);
    
    setEditorState(finalEditorState);
  };

  // Style map for font sizes - comprehensive range for all possible values
  const styleMap = {
    'FONTSIZE_10': { fontSize: '10px' },
    'FONTSIZE_11': { fontSize: '11px' },
    'FONTSIZE_12': { fontSize: '12px' },
    'FONTSIZE_13': { fontSize: '13px' },
    'FONTSIZE_14': { fontSize: '14px' },
    'FONTSIZE_15': { fontSize: '15px' },
    'FONTSIZE_16': { fontSize: '16px' },
    'FONTSIZE_17': { fontSize: '17px' },
    'FONTSIZE_18': { fontSize: '18px' },
    'FONTSIZE_19': { fontSize: '19px' },
    'FONTSIZE_20': { fontSize: '20px' },
    'FONTSIZE_21': { fontSize: '21px' },
    'FONTSIZE_22': { fontSize: '22px' },
    'FONTSIZE_23': { fontSize: '23px' },
    'FONTSIZE_24': { fontSize: '24px' },
  };

  // Handle keyboard shortcuts
  const handleKeyCommand = (command) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate year range
    if (startYear && endYear && parseInt(startYear) > parseInt(endYear)) {
      alert('Start year cannot be greater than end year');
      return;
    }
    
    // Validate parent-child logic
    if (!isParent && !parentId) {
      alert('Child amplifiers must have a parent selected');
      return;
    }
    
    if (isParent && parentId) {
      alert('Parent amplifiers cannot have a parent selected');
      return;
    }
    
    let descriptionValue;
    
    if (useRawHtmlMode && activeTab === 'html') {
      // Store raw HTML directly with a marker prefix so we know it's raw HTML
      descriptionValue = `__RAW_HTML__${htmlContent}`;
    } else {
      // Convert to Draft.js format
      let finalEditorState = editorState;
      if (activeTab === 'html') {
        finalEditorState = setEditorStateFromHtml(htmlContent);
      }
      descriptionValue = JSON.stringify(convertToRaw(finalEditorState.getCurrentContent()));
    }
    
    onSave({
      manufacturer_id: manufacturerId,
      template_id: templateId || null,
      name,
      model,
      start_year: startYear || null,
      end_year: endYear || null,
      description: descriptionValue,
      serial_number: serialNumber,
      is_parent: isParent,
      parent_id: parentId || null
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div>
        <label>Manufacturer:</label>
        <select
          value={manufacturerId}
          onChange={e => setManufacturerId(e.target.value)}
          required
        >
          <option value="">Select Manufacturer</option>
          {manufacturers.map(m => (
            <option key={m.manufacturer_id} value={m.manufacturer_id}>
              {m.name || m.manufacturer_name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label>Template:</label>
        <select
          value={templateId}
          onChange={e => setTemplateId(e.target.value)}
        >
          <option value="">Select Template (Optional)</option>
          {templates.map(t => (
            <option key={t.template_id} value={t.template_id}>
              {t.template_name}
            </option>
          ))}
        </select>
      </div>
      
      {/* Parent-Child Selection Section */}
      <div style={{ border: '1px solid #ddd', padding: '1rem', borderRadius: '4px', margin: '1rem 0' }}>
        <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>Amplifier Type</h4>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <input
              type="radio"
              name="amplifierType"
              checked={isParent}
              onChange={() => {
                setIsParent(true);
                setParentId('');
              }}
              style={{ marginRight: '0.5rem' }}
            />
            <span style={{ fontWeight: 'bold' }}>Parent/Specification</span>
            <span style={{ marginLeft: '0.5rem', fontSize: '0.9em', color: '#666' }}>
              (OEM specification, manual, catalog entry)
            </span>
          </label>
          
          <label style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="radio"
              name="amplifierType"
              checked={!isParent}
              onChange={() => setIsParent(false)}
              style={{ marginRight: '0.5rem' }}
            />
            <span style={{ fontWeight: 'bold' }}>Individual Unit</span>
            <span style={{ marginLeft: '0.5rem', fontSize: '0.9em', color: '#666' }}>
              (Specific manufactured amplifier)
            </span>
          </label>
        </div>
        
        {!isParent && (
          <div>
            <label>Parent Specification:</label>
            <select
              value={parentId}
              onChange={e => setParentId(e.target.value)}
              required={!isParent}
              style={{ width: '100%', padding: '0.5rem' }}
            >
              <option value="">Select Parent Specification</option>
              {parentAmplifiers
                .filter(p => p.manufacturer_id === manufacturerId || !manufacturerId) // Filter by manufacturer if selected
                .map(p => (
                <option key={p.amplifier_id} value={p.amplifier_id}>
                  {p.manufacturer_name} - {p.name} {p.model ? `(${p.model})` : ''}
                </option>
              ))}
            </select>
            {manufacturerId && parentAmplifiers.filter(p => p.manufacturer_id === manufacturerId).length === 0 && (
              <p style={{ fontSize: '0.9em', color: '#666', margin: '0.5rem 0 0 0' }}>
                No parent specifications available for this manufacturer. Create a parent specification first.
              </p>
            )}
          </div>
        )}
      </div>
      
      <div>
        <label>Name:</label>
        <input value={name} onChange={e => setName(e.target.value)} />
      </div>
      <div>
        <label>Model:</label>
        <input value={model} onChange={e => setModel(e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{ flex: 1 }}>
          <label>Start Year:</label>
          <input 
            type="number" 
            value={startYear} 
            onChange={e => setStartYear(e.target.value)} 
            placeholder="e.g., 1965"
            min="1900"
            max="2030"
          />
        </div>
        <div style={{ flex: 1 }}>
          <label>End Year:</label>
          <input 
            type="number" 
            value={endYear} 
            onChange={e => setEndYear(e.target.value)} 
            placeholder="e.g., 1972"
            min="1900"
            max="2030"
          />
        </div>
      </div>
      <div style={{ fontSize: '0.85em', color: '#666', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
        Leave end year blank if same as start year, or if still in production
      </div>
      <div>
        <label>Serial Number:</label>
        <input value={serialNumber} onChange={e => setSerialNumber(e.target.value)} />
      </div>
      <div>
        <label>Description:</label>
        
        {/* Tab Buttons */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '2px solid #ddd',
          marginBottom: '0'
        }}>
          <button
            type="button"
            onClick={() => handleTabSwitch('visual')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: activeTab === 'visual' ? '3px solid #007bff' : '3px solid transparent',
              backgroundColor: activeTab === 'visual' ? '#f8f9fa' : 'white',
              color: activeTab === 'visual' ? '#007bff' : '#666',
              fontWeight: activeTab === 'visual' ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '-2px'
            }}
          >
            Visual
          </button>
          <button
            type="button"
            onClick={() => handleTabSwitch('html')}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderBottom: activeTab === 'html' ? '3px solid #007bff' : '3px solid transparent',
              backgroundColor: activeTab === 'html' ? '#f8f9fa' : 'white',
              color: activeTab === 'html' ? '#007bff' : '#666',
              fontWeight: activeTab === 'html' ? '600' : '400',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: '-2px'
            }}
          >
            &lt;/&gt; HTML
          </button>
        </div>
        
        {activeTab === 'visual' ? (
          <>
            {/* Rich Text Toolbar */}
            <div style={{ 
              border: '1px solid #ccc', 
              borderBottom: 'none', 
              backgroundColor: '#f8f9fa', 
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              {/* Bold Button */}
              <button
                type="button"
                onClick={toggleBold}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ddd',
                  backgroundColor: editorState.getCurrentInlineStyle().has('BOLD') ? '#007bff' : 'white',
                  color: editorState.getCurrentInlineStyle().has('BOLD') ? 'white' : 'black',
                  cursor: 'pointer',
                  borderRadius: '3px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!editorState.getCurrentInlineStyle().has('BOLD')) {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!editorState.getCurrentInlineStyle().has('BOLD')) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
                title="Bold (Ctrl+B)"
              >
                B
              </button>

              {/* Italic Button */}
              <button
                type="button"
                onClick={toggleItalic}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ddd',
                  backgroundColor: editorState.getCurrentInlineStyle().has('ITALIC') ? '#007bff' : 'white',
                  color: editorState.getCurrentInlineStyle().has('ITALIC') ? 'white' : 'black',
                  cursor: 'pointer',
                  borderRadius: '3px',
                  fontStyle: 'italic',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!editorState.getCurrentInlineStyle().has('ITALIC')) {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!editorState.getCurrentInlineStyle().has('ITALIC')) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
                title="Italic (Ctrl+I)"
              >
                I
              </button>

              {/* Underline Button */}
              <button
                type="button"
                onClick={toggleUnderline}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ddd',
                  backgroundColor: editorState.getCurrentInlineStyle().has('UNDERLINE') ? '#007bff' : 'white',
                  color: editorState.getCurrentInlineStyle().has('UNDERLINE') ? 'white' : 'black',
                  cursor: 'pointer',
                  borderRadius: '3px',
                  textDecoration: 'underline',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!editorState.getCurrentInlineStyle().has('UNDERLINE')) {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!editorState.getCurrentInlineStyle().has('UNDERLINE')) {
                    e.target.style.backgroundColor = 'white';
                  }
                }}
                title="Underline (Ctrl+U)"
              >
                U
              </button>

              {/* Font Size Controls */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '12px', marginRight: '8px' }}>Font Size:</span>
                <input
                  type="number"
                  value={fontSize}
                  onChange={(e) => {
                    const newSize = Math.min(Math.max(parseInt(e.target.value) || 10, 10), 24);
                    setFontSize(newSize);
                    applyFontSize(newSize);
                  }}
                  style={{
                    width: '60px',
                    padding: '4px',
                    border: '1px solid #ddd',
                    borderRadius: '3px',
                    textAlign: 'center',
                    fontSize: '12px'
                  }}
                  min="10"
                  max="24"
                  step="1"
                  title="Font Size in Pixels"
                />
                <span style={{ fontSize: '11px', color: '#666' }}>px</span>
              </div>
            </div>

            {/* Visual Editor */}
            <div style={{ border: '1px solid #ccc', minHeight: 100, padding: 8 }}>
              <Editor 
                editorState={editorState} 
                onChange={setEditorState}
                customStyleMap={styleMap}
                handleKeyCommand={handleKeyCommand}
                placeholder="Enter description..."
              />
            </div>
          </>
        ) : (
          <>
            {/* HTML Editor Header */}
            <div style={{ 
              border: '1px solid #ccc', 
              borderBottom: 'none', 
              backgroundColor: '#f8f9fa', 
              padding: '8px 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              <div style={{ fontSize: '12px', color: '#666' }}>
                Tip: Edit the raw HTML below. Switch to Visual tab to see how it renders.
              </div>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                backgroundColor: useRawHtmlMode ? '#d4edda' : '#fff3cd',
                padding: '4px 10px',
                borderRadius: '4px',
                border: useRawHtmlMode ? '1px solid #28a745' : '1px solid #ffc107'
              }}>
                <input
                  type="checkbox"
                  checked={useRawHtmlMode}
                  onChange={(e) => setUseRawHtmlMode(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontWeight: '500', color: useRawHtmlMode ? '#155724' : '#856404' }}>
                  {useRawHtmlMode ? '✓ Raw HTML Mode (preserves exact HTML)' : 'Use Raw HTML Mode'}
                </span>
              </label>
            </div>
            {useRawHtmlMode && (
              <div style={{
                backgroundColor: '#d4edda',
                border: '1px solid #c3e6cb',
                borderTop: 'none',
                padding: '8px 12px',
                fontSize: '11px',
                color: '#155724'
              }}>
                <strong>Raw HTML Mode enabled:</strong> Your HTML will be saved exactly as entered, preserving all formatting. 
                The Visual tab may not display complex HTML correctly, but it will be stored and rendered properly.
              </div>
            )}
            <textarea
              value={htmlContent}
              onChange={handleHtmlChange}
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '12px',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                fontSize: '13px',
                lineHeight: '1.5',
                border: '1px solid #ccc',
                borderTop: 'none',
                borderRadius: '0 0 4px 4px',
                resize: 'vertical',
                backgroundColor: '#2d2d2d',
                color: '#f8f8f2'
              }}
              placeholder="<p>Enter HTML here...</p>"
            />
            {/* HTML Preview */}
            <div style={{ marginTop: '1rem' }}>
              <div style={{ 
                fontSize: '12px', 
                color: '#666', 
                marginBottom: '4px',
                fontWeight: '600'
              }}>
                Preview:
              </div>
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '12px',
                  backgroundColor: '#fafafa',
                  minHeight: '60px'
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent || '<em style="color: #999;">No content</em>' }}
              />
            </div>
          </>
        )}
      </div>
      <button type="submit">Save</button>
      <button type="button" onClick={onCancel} style={{ marginLeft: 8 }}>Cancel</button>
    </form>
  );
};

export default AmplifierForm;